/**
 * DropdownPicker — compact trigger button + floating popover.
 * Used by ScopeSelector and ModelPicker in compact (3-column) mode.
 *
 * The popover renders via createPortal to document.body with fixed positioning,
 * preventing clipping by parent overflow containers (e.g. AI Workspace .main).
 */

import { useState, useEffect, useRef, useCallback, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import styles from './DropdownPicker.module.css';

interface Props {
  selectedLabel: string;
  selectedSublabel?: string;
  children: ReactNode;
  open: boolean;
  onToggle: () => void;
}

interface PopoverPos {
  top?: number;
  bottom?: number;
  left: number;
  width: number;
  maxHeight: number;
}

export function DropdownPicker({ selectedLabel, selectedSublabel, children, open, onToggle }: Props) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<PopoverPos | null>(null);

  // Calculate popover position from trigger's bounding rect
  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - 8;
    const spaceAbove = rect.top - 8;
    const flipUp = spaceBelow < 120 && spaceAbove > spaceBelow;

    setPos({
      left: rect.left,
      width: Math.max(rect.width, 180),
      ...(flipUp
        ? { bottom: window.innerHeight - rect.top + 4, maxHeight: Math.min(280, spaceAbove) }
        : { top: rect.bottom + 4, maxHeight: Math.min(280, spaceBelow) }),
    });
  }, []);

  // Position on open + reposition on scroll/resize
  useEffect(() => {
    if (!open) { setPos(null); return; }
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open, updatePosition]);

  // Close on outside pointer / Escape
  useEffect(() => {
    if (!open) return;
    const handlePointer = (e: PointerEvent | MouseEvent) => {
      const target = e.target as Node;
      if (
        wrapperRef.current && !wrapperRef.current.contains(target) &&
        (!popoverRef.current || !popoverRef.current.contains(target))
      ) {
        onToggle();
      }
    };
    // 20260921 - AN OPEN PICKER OWNS THE PRESS (P3-18a). This used to be a plain `document` bubble
    // listener with no stopPropagation, so ONE Escape ran both handlers: the popover closed AND
    // useKeyboardShortcuts' window listener fired closeAiWorkspace(), which unmounts the flow and
    // discards the unpersisted Create prompt the user had typed. Dismissing a popover must not close
    // the surface behind it.
    // WINDOW + capture, deliberately, not document + stopPropagation: window-capture is the FIRST
    // node in the dispatch path, so it also beats React's delegated root-container onKeyDown
    // handlers (the board Enhance dialog's) — a document-level stopPropagation would not, because
    // React 19 attaches its listeners to the render container, which sits below document. The
    // useKeyboardShortcuts / SceneBoardPage listeners are window BUBBLE, i.e. the last node in the
    // path, so stopPropagation from here reaches them (same-node listeners would need
    // stopImmediatePropagation; there are none). preventDefault is belt-and-braces for
    // SceneBoardPage's DOM entry, which bails on e.defaultPrevented.
    // Fixes every consumer at once: CreateTab, TouchUpTab, StyleGradeTab, ModelPicker,
    // ScopeSelector, ScratchPanel, PromptPopover and the board dialog.
    // RESIDUAL: Unity's HARDWARE Escape arrives as window.__requestBack and touches no DOM listener
    // at all, so on device that press still falls to SceneBoardPage's closeOpenWorkspace() rung.
    // Closing that needs a parked-closer rung in the ladder (the shotPreviewEsc.ts pattern) and is
    // deliberately out of this fix.
    const handleKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      e.preventDefault();
      onToggle();
    };
    document.addEventListener('pointerdown', handlePointer);
    window.addEventListener('keydown', handleKey, true);
    return () => {
      document.removeEventListener('pointerdown', handlePointer);
      window.removeEventListener('keydown', handleKey, true);
    };
  }, [open, onToggle]);

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <button
        ref={triggerRef}
        type="button"
        className={`${styles.trigger} ${open ? styles.triggerOpen : ''}`}
        onClick={onToggle}
      >
        <span className={styles.triggerLabel}>
          {selectedLabel}
          {selectedSublabel && <span className={styles.triggerSublabel}>{selectedSublabel}</span>}
        </span>
        <span className={`${styles.chevron} ${open ? styles.chevronOpen : ''}`}>&#9660;</span>
      </button>
      {open && pos && createPortal(
        <div
          ref={popoverRef}
          className={styles.popover}
          style={{
            position: 'fixed',
            left: pos.left,
            width: pos.width,
            maxHeight: pos.maxHeight,
            ...(pos.top != null ? { top: pos.top } : {}),
            ...(pos.bottom != null ? { bottom: pos.bottom } : {}),
          }}
        >
          {children}
        </div>,
        document.body,
      )}
    </div>
  );
}
